/**
 * RoleModal — Add / Edit Role centered modal (May 2026 Figma).
 *
 * Layout:
 *   ┌── Add role / Edit role ────────────────────────────────────┐
 *   │ ┌─ General ──────────┐  ┌─ Tab content ──────────────────┐│
 *   │ │ General settings   │  │ Section header + All toggle   ││
 *   │ │ Devices            │  │ Card panel of permission rows ││
 *   │ ├─ Software perms ───┤  └────────────────────────────────┘│
 *   │ │ System and sites   │                                    │
 *   │ │ Users              │                                    │
 *   │ │ Comms              │                                    │
 *   │ │ Location           │                                    │
 *   │ │ Panic              │                                    │
 *   │ │ Dashboards         │                                    │
 *   │ │ Checkout           │                                    │
 *   │ └────────────────────┘             [ Cancel ]  [ Add ]    │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Props:
 *   open    : boolean
 *   role    : existing role → EDIT mode; null/undefined → ADD
 *   onClose : () => void
 *   onSaved : () => void   — fired after a successful create/update
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import employeeRoleService from '../../services/employeeRoleService';
import organizationService from '../../services/organizationService';
import { useTenant } from '../../context/TenantContext';

// ----- Permission section schema -----------------------------------------
// Each item has key, label, desc, optional `parent` (top-level emphasis) or
// `indent` (sub-row indented under the parent).

const LEVELS = ['None', 'View', 'Edit'];

const PERMISSION_SCHEMA = {
  devices: {
    title: 'DEVICES',
    items: [
      { key: 'devices',            label: 'Devices',      desc: 'Top-level device management and monitoring', parent: true },
      { key: 'devicesBodyCameras', label: 'Body cameras', desc: 'View and manage body camera devices', indent: true },
      { key: 'devicesDocks',       label: 'Docks',        desc: 'View and manage docks', indent: true },
      { key: 'devicesProfiles',    label: 'Profiles',     desc: 'Body cameras profiles management', indent: true },
      { key: 'devicesUpdates',     label: 'Updates',      desc: 'Firmware and software updates', indent: true },
      { key: 'devicesNetwork',     label: 'Network',      desc: 'Network configuration', indent: true },
    ],
  },
  systemSites: {
    groups: [
      {
        title: 'SYSTEM',
        items: [
          { key: 'system',             label: 'System',       desc: 'System-wide settings and configuration', parent: true },
          { key: 'systemOrganization', label: 'Organization', desc: 'Organization level settings', indent: true },
          { key: 'systemLicenses',     label: 'Licenses',     desc: 'Licenses management', indent: true },
        ],
      },
      {
        title: 'SITES',
        items: [
          { key: 'sites', label: 'Sites', desc: 'Manage site locations and settings' },
        ],
      },
    ],
  },
  users: {
    title: 'USERS',
    items: [
      { key: 'usersTop',    label: 'Users', desc: 'Top-level user management', parent: true },
      { key: 'usersManage', label: 'Users', desc: 'Manage user account', indent: true },
      { key: 'roles',       label: 'Roles', desc: 'Roles and permission configuration', indent: true },
    ],
  },
  comms: {
    title: 'COMMS',
    items: [
      { key: 'comms',           label: 'Comms',       desc: 'Top-level Teams and Talk groups management', parent: true },
      { key: 'commsTeams',      label: 'Teams',       desc: 'View and manage teams', indent: true },
      { key: 'commsTalkGroups', label: 'Talk groups', desc: 'Talk group management', indent: true },
    ],
  },
  location: {
    title: 'LOCATION',
    items: [
      { key: 'location', label: 'Location', desc: 'Real-time location on map and tracking' },
    ],
  },
  panic: {
    title: 'PANIC',
    items: [
      { key: 'panic', label: 'Panic', desc: 'Emergency / panic behavior configuration' },
    ],
  },
  dashboards: {
    title: 'DASHBOARDS',
    items: [
      { key: 'dashboards',          label: 'Dashboards', desc: 'Top-level dashboards permissions', parent: true },
      { key: 'dashboardsActivity',  label: 'Activity overview', desc: 'Main overview dashboard', indent: true },
      { key: 'dashboardsDevices',   label: 'Devices',    desc: 'Body cameras and Docks monitoring dashboard', indent: true },
      { key: 'dashboardsCalls',     label: 'Calls',      desc: 'Calls activity dashboard', indent: true },
      { key: 'dashboardsSites',     label: 'Sites',      desc: 'Site activity overview', indent: true },
      { key: 'dashboardsEmergency', label: 'Emergency',  desc: 'Device monitoring dashboard', indent: true },
    ],
  },
  checkout: {
    title: 'CHECKOUTS',
    items: [
      { key: 'checkouts',               label: 'Checkouts',           desc: 'Top-level checkouts management', parent: true },
      { key: 'checkoutsManage',         label: 'Checkouts',           desc: 'View and manage checkouts', indent: true },
      { key: 'checkoutsTemplates',      label: 'Checkout template',   desc: 'Check out template management', indent: true },
      { key: 'checkoutsApplyTemplates', label: 'Apply checkout template', desc: 'Change and apply checkout template', indent: true },
    ],
  },
};

// Collect every permission key across all sections so we can seed a default
// "all NONE" map.
const ALL_PERM_KEYS = (() => {
  const out = [];
  Object.values(PERMISSION_SCHEMA).forEach((sec) => {
    if (sec.items) out.push(...sec.items.map((i) => i.key));
    if (sec.groups) sec.groups.forEach((g) => out.push(...g.items.map((i) => i.key)));
  });
  return [...new Set(out)];
})();

const SIDE_TABS = [
  { id: 'general',     label: 'General settings', group: 'General' },
  { id: 'devices',     label: 'Devices',         group: 'General' },
  { id: 'systemSites', label: 'System and sites', group: 'Software permisions' },
  { id: 'users',       label: 'Users',            group: 'Software permisions' },
  { id: 'comms',       label: 'Comms',            group: 'Software permisions' },
  { id: 'location',    label: 'Location',         group: 'Software permisions' },
  { id: 'panic',       label: 'Panic',            group: 'Software permisions' },
  { id: 'dashboards',  label: 'Dashboards',       group: 'Software permisions' },
  { id: 'checkout',    label: 'Checkout',         group: 'Software permisions' },
];

const DEFAULT_FORM = {
  roleName: '',
  description: '',
  twoFactor: false,
  siteRestriction: 'all',         // 'all' | 'specific' | 'dynamic'
  assignment: 'checkout',          // 'checkout' maps to backend DYNAMIC / User Sites; 'manual' maps to SPECIFIC
  siteIds: [],
  // Body-camera on/off booleans (General settings tab)
  bodyCameraUser: false,
  bluetoothItems: false,
  prioritySpeaker: false,
  broadcaster: false,
  // Permissions map: key -> 'NONE' | 'VIEW' | 'EDIT'
  permissions: ALL_PERM_KEYS.reduce((m, k) => ({ ...m, [k]: 'NONE' }), {}),
};

// ----- Small UI primitives -------------------------------------------------

function HelpIcon() {
  return <i className="far fa-question-circle os-help-icon" aria-hidden="true" />;
}

function ToggleSwitch({ checked, onChange, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      className={`rm-toggle ${checked ? 'on' : 'off'}`}
      onClick={() => onChange(!checked)}
    >
      <span className="rm-toggle-knob" />
    </button>
  );
}

function PermissionSlider({ levels = LEVELS, value, onChange }) {
  const upper = levels.map((l) => l.toUpperCase());
  const idx = upper.indexOf(String(value).toUpperCase());
  const safeIdx = idx >= 0 ? idx : 0;
  return (
    <div className={`rm-slider rm-slider-stops-${levels.length}`}>
      <div className="rm-slider-track" />
      <div
        className="rm-slider-fill"
        style={{ width: levels.length === 1 ? '0' : `${(safeIdx / (levels.length - 1)) * 100}%` }}
      />
      <div className="rm-slider-row">
        {levels.map((lvl, i) => (
          <button
            key={lvl}
            type="button"
            className={`rm-slider-stop ${i <= safeIdx ? 'on' : ''} ${i === safeIdx ? 'active' : ''}`}
            onClick={() => onChange(upper[i])}
            aria-label={lvl}
          />
        ))}
      </div>
      <div className="rm-slider-labels">
        {levels.map((lvl, i) => (
          <span key={lvl} className={`rm-slider-label ${i === safeIdx ? 'active' : ''}`}>{lvl}</span>
        ))}
      </div>
    </div>
  );
}

/** Multi-select dropdown for the Sites picker (local to RoleModal). */
function SitesMultiSelect({ options, value, onChange, placeholder = 'Select site' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const selected = Array.isArray(value) ? value : [];
  const selectedKeys = selected.map(String);
  const allSelected  = options.length > 0 && options.every((o) => selectedKeys.includes(String(o.value)));
  const someSelected = selectedKeys.length > 0 && !allSelected;
  const toggleAll = () => onChange(allSelected ? [] : options.map((o) => o.value));
  const toggle = (v) => {
    const k = String(v);
    onChange(selectedKeys.includes(k)
      ? selected.filter((x) => String(x) !== k)
      : [...selected, v]);
  };
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return !q ? options : options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, search]);
  const triggerLabel = selected.length === 0
    ? placeholder
    : selected.map((v) => options.find((o) => String(o.value) === String(v))?.label || v).join(', ');
  return (
    <div className={`sites-mselect ${open ? 'open' : ''}`} ref={ref}>
      <button
        type="button"
        className="sites-mselect-trigger"
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selected.length ? '' : 'sites-mselect-placeholder'}>{triggerLabel}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} />
      </button>
      {open && (
        <div className="sites-mselect-menu">
          <div className="sites-mselect-caption">SELECT SITES FROM THE LIST</div>
          <div className="tg-sites-search">
            <i className="fas fa-search" />
            <input type="text" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {options.length > 0 && (
            <button
              type="button"
              className="sites-mselect-row sites-mselect-row-all"
              onClick={toggleAll}
            >
              <span className={`sites-mselect-box ${allSelected ? 'checked' : ''} ${someSelected ? 'indeterminate' : ''}`}>
                {allSelected && <i className="fas fa-check" />}
                {someSelected && <i className="fas fa-minus" />}
              </span>
              <span className="sites-mselect-label sites-mselect-label-strong">Select all sites</span>
            </button>
          )}
          {filtered.length === 0 && <div className="sites-mselect-empty">No options</div>}
          {filtered.map((o) => {
            const checked = selectedKeys.includes(String(o.value));
            return (
              <button
                type="button"
                key={o.value}
                className={`sites-mselect-row ${checked ? 'checked' : ''}`}
                onClick={() => toggle(o.value)}
              >
                <span className={`sites-mselect-box ${checked ? 'checked' : ''}`}>
                  {checked && <i className="fas fa-check" />}
                </span>
                <span className="sites-mselect-label">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ----- Section rendering helpers ------------------------------------------

function PermSection({ title, items, perms, setPerm, levels = LEVELS, hideMaster = false }) {
  // "All permissions" toggle reflects: every item in the section is at the
  // highest level (Edit / On).
  const maxLevel = levels[levels.length - 1].toUpperCase();
  const allOn = items.every((it) => perms[it.key] === maxLevel);
  const toggleAll = (checked) => {
    const next = checked ? maxLevel : 'NONE';
    items.forEach((it) => setPerm(it.key, next));
  };
  return (
    <div className="rm-perm-section">
      <div className="rm-section-head">
        <span className="rm-section-title">{title}</span>
        {!hideMaster && (
          <div className="rm-section-all">
            <ToggleSwitch
              checked={allOn}
              onChange={toggleAll}
              ariaLabel={`Toggle all permissions in ${title}`}
            />
            <span className="rm-section-all-label">All permissions</span>
          </div>
        )}
      </div>
      <div className="rm-card">
        {items.map((it) => (
          <div
            key={it.key}
            className={`rm-perm-row ${it.indent ? 'indent' : ''} ${it.parent ? 'parent' : ''}`}
          >
            <div className="rm-perm-meta">
              <div className="rm-perm-name">{it.label}</div>
              <div className="rm-perm-desc">{it.desc}</div>
            </div>
            <div className="rm-perm-control">
              <PermissionSlider
                levels={levels}
                value={perms[it.key] || 'NONE'}
                onChange={(v) => setPerm(it.key, v)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----- Form prefill --------------------------------------------------------

function formFromRole(r) {
  if (!r) return DEFAULT_FORM;
  const mode = String(r.siteAssociationMode || '').toUpperCase();
  const bodyCameraUser = !!r.bodyCameraUser;
  const bluetoothItems = !!r.bluetoothItems;
  const prioritySpeaker = !!r.prioritySpeaker;
  const broadcaster = !!r.broadcaster;
  return {
    ...DEFAULT_FORM,
    roleName:        r.roleName || r.name || '',
    description:     r.description || '',
    twoFactor:       !!(r.twoFactorAuth ?? r.twoFactor),
    siteRestriction: (r.allSites || r.appliesToAllSites || mode === 'ALL_SITES') ? 'all' : 'specific',
    assignment:      mode === 'DYNAMIC' ? 'checkout' : (r.assignment || 'manual'),
    siteIds:         r.siteIds || [],
    bodyCameraUser,
    bluetoothItems,
    prioritySpeaker,
    broadcaster,
    permissions: {
      ...DEFAULT_FORM.permissions,
      ...(r.uiPermissionMap || {}),
    },
  };
}

// ----- Main component ------------------------------------------------------

export default function RoleModal({ open, role, onClose, onSaved }) {
  const { orgId, sites: tenantSites } = useTenant();
  const isEdit = !!role;

  const [tab, setTab] = useState('general');
  const [form, setForm] = useState(DEFAULT_FORM);
  const [sites, setSites] = useState([]);
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(formFromRole(role));
    setTab('general');
    setTouched({});
    setError(null);
    setSubmitting(false);
  }, [open, role]);

  useEffect(() => {
    if (!open || !orgId) return undefined;
    let cancelled = false;
    setSites(Array.isArray(tenantSites) ? tenantSites : []);
    (async () => {
      try {
        const res = await organizationService.listSites(orgId, 0, 500);
        if (cancelled) return;
        const list = Array.isArray(res) ? res : (res?.content || res?.items || []);
        setSites(Array.isArray(list) ? list : []);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, orgId, tenantSites]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const setPerm  = (key, value) => setForm((p) => ({ ...p, permissions: { ...p.permissions, [key]: value } }));
  const setBodyCameraPerm = (key, checked) => setForm((p) => {
    const next = {
      bodyCameraUser: key === 'bodyCameraUser' ? checked : p.bodyCameraUser,
      bluetoothItems: key === 'bluetoothItems' ? checked : p.bluetoothItems,
      prioritySpeaker: key === 'prioritySpeaker' ? checked : p.prioritySpeaker,
      broadcaster: key === 'broadcaster' ? checked : p.broadcaster,
    };
    return {
      ...p,
      ...next,
    };
  });

  const errs = {
    roleName: !form.roleName.trim() ? 'This field is required' : null,
  };
  const hasErrors = Object.values(errs).some(Boolean);

  const handleSubmit = async () => {
    setTouched({ roleName: true });
    if (hasErrors || submitting || !orgId) return;
    setSubmitting(true);
    setError(null);
    try {
      const siteAssociationMode = form.siteRestriction === 'all'
        ? 'ALL_SITES'
        : (form.assignment === 'checkout' ? 'DYNAMIC' : 'SPECIFIC');
      const payload = {
        orgId,
        roleName:       form.roleName.trim(),
        description:    form.description?.trim() || undefined,
        twoFactorAuth:  form.twoFactor,
        siteAssociationMode,
        allSites:       siteAssociationMode === 'ALL_SITES',
        assignment:     form.siteRestriction === 'specific' ? form.assignment : undefined,
        siteIds:        siteAssociationMode === 'SPECIFIC' ? form.siteIds : [],
        bodyCameraUser: form.bodyCameraUser,
        bluetoothItems: form.bluetoothItems,
        prioritySpeaker: form.prioritySpeaker,
        broadcaster:    form.broadcaster,
        uiPermissionMap: form.permissions,
      };
      if (isEdit) {
        const id = role.roleId ?? role.id;
        await employeeRoleService.updateRole(id, payload, orgId);
      } else {
        await employeeRoleService.createRole(payload, orgId);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message
        ?? err?.response?.data?.error
        ?? err.message
        ?? (isEdit ? 'Failed to update role' : 'Failed to create role'),
      );
      setSubmitting(false);
    }
  };

  const siteOptions = (sites || []).map((s) => ({
    value: s.siteId ?? s.id,
    label: s.siteName ?? s.name ?? `Site ${s.siteId ?? s.id}`,
  }));

  // Build the left rail grouped by `group`.
  const grouped = SIDE_TABS.reduce((acc, t) => {
    if (!acc[t.group]) acc[t.group] = [];
    acc[t.group].push(t);
    return acc;
  }, {});

  // -- Render the active tab body --------------------------------------------
  const renderTab = () => {
    if (tab === 'general') {
      return (
        <div className="rm-tab-body">
          <div className="rm-section-head"><span className="rm-section-title">INFORMATION</span></div>
          <div className="rm-card rm-info-card">
            <div className="rm-field">
              <label className="rm-label">Role name<span className="os-req">*</span></label>
              <input
                className={touched.roleName && errs.roleName ? 'os-input os-input-error' : 'os-input'}
                placeholder="Role name"
                value={form.roleName}
                onChange={(e) => setField('roleName', e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, roleName: true }))}
              />
              {touched.roleName && errs.roleName && (
                <div className="os-field-error"><i className="fas fa-info-circle" /> {errs.roleName}</div>
              )}
            </div>

            <div className="rm-field">
              <div className="rm-row-between">
                <label className="rm-label">Two Factor Authentication<span className="os-req">*</span> <HelpIcon /></label>
                <ToggleSwitch
                  checked={form.twoFactor}
                  onChange={(v) => setField('twoFactor', v)}
                  ariaLabel="Two Factor Authentication"
                />
              </div>
            </div>

            <div className="rm-field">
              <label className="rm-label">Site Restrictions <HelpIcon /></label>
              <div className="tg-segmented rm-site-restrictions">
                <button
                  type="button"
                  className={`tg-segmented-btn ${form.siteRestriction === 'all' ? 'active' : ''}`}
                  onClick={() => setField('siteRestriction', 'all')}
                >All Sites</button>
                <button
                  type="button"
                  className={`tg-segmented-btn ${form.siteRestriction === 'specific' ? 'active' : ''}`}
                  onClick={() => setField('siteRestriction', 'specific')}
                >Specific Sites</button>
              </div>
            </div>

            {form.siteRestriction === 'specific' && (
              <>
                <div className="rm-field">
                  <label className="rm-label">Assignment <HelpIcon /></label>
                  <div className="tg-segmented">
                    <button
                      type="button"
                      className={`tg-segmented-btn ${form.assignment === 'checkout' ? 'active' : ''}`}
                      onClick={() => setField('assignment', 'checkout')}
                    >User Sites</button>
                    <button
                      type="button"
                      className={`tg-segmented-btn ${form.assignment === 'manual' ? 'active' : ''}`}
                      onClick={() => setField('assignment', 'manual')}
                    >Manual</button>
                  </div>
                </div>

                {form.assignment === 'manual' && (
                  <div className="rm-field">
                    <label className="rm-label">Sites <HelpIcon /></label>
                    <SitesMultiSelect
                      options={siteOptions}
                      value={form.siteIds}
                      onChange={(v) => setField('siteIds', v)}
                      placeholder="Select site"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <PermSection
            title="BODY CAMERA PERMISSIONS"
            items={[
              { key: 'bodyCameraUser',  label: 'Body camera user',  desc: 'User appears on the checkout list for all associated sites' },
              { key: 'bluetoothItems',  label: 'Bluetooth',         desc: 'Allow users to connect Bluetooth accessories' },
              { key: 'prioritySpeaker', label: 'Priority Speaker',  desc: 'Allows this user transmission priority over others in the talk group' },
              { key: 'broadcaster',     label: 'Broadcaster',       desc: 'Allows this user to speak in broadcast talk groups.' },
            ]}
            perms={{
              bodyCameraUser:  form.bodyCameraUser  ? 'ON' : 'OFF',
              bluetoothItems:  form.bluetoothItems  ? 'ON' : 'OFF',
              prioritySpeaker: form.prioritySpeaker ? 'ON' : 'OFF',
              broadcaster:     form.broadcaster     ? 'ON' : 'OFF',
            }}
            setPerm={(k, v) => {
              setBodyCameraPerm(k, v === 'ON');
            }}
            levels={['Off', 'On']}
          />
        </div>
      );
    }

    if (tab === 'systemSites') {
      const sec = PERMISSION_SCHEMA.systemSites;
      return (
        <div className="rm-tab-body">
          {sec.groups.map((g) => (
            <PermSection
              key={g.title}
              title={g.title}
              items={g.items}
              perms={form.permissions}
              setPerm={setPerm}
            />
          ))}
        </div>
      );
    }

    const sec = PERMISSION_SCHEMA[tab];
    if (!sec) return null;
    return (
      <div className="rm-tab-body">
        <PermSection
          title={sec.title}
          items={sec.items}
          perms={form.permissions}
          setPerm={setPerm}
        />
      </div>
    );
  };

  return (
    <div
      className="os-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit role' : 'Add role'}
      onMouseDown={(e) => e.target === e.currentTarget && !submitting && onClose?.()}
    >
      <div className="os-modal rm-modal" onClick={(e) => e.stopPropagation()}>
        <header className="os-modal-header">
          <h2>{isEdit ? 'Edit role' : 'Add role'}</h2>
        </header>

        <div className="os-modal-body">
          <aside className="os-side rm-side">
            {Object.entries(grouped).map(([groupName, tabs]) => (
              <div key={groupName} className="rm-side-group">
                <div className="rm-side-group-title">{groupName}</div>
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`os-side-item rm-side-item ${tab === t.id ? 'active' : ''}`}
                    onClick={() => setTab(t.id)}
                  >
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </aside>

          <section className="os-pane rm-pane">
            {renderTab()}
          </section>
        </div>

        {error && <div className="os-modal-error">{error}</div>}

        <footer className="os-modal-footer">
          <button type="button" className="btn-ghost-small" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary-pill"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (isEdit ? 'Saving…' : 'Adding…') : (isEdit ? 'Save' : 'Add')}
          </button>
        </footer>
      </div>
    </div>
  );
}
